"use client";

import IndexedStroage from "@/images/product-images/indexed-storage.png";
import StackingCabinets from "@/images/product-images/stacked-cabinets.gif";
import Trays from "@/images/product-images/trays/trays-border.png";
import { cn } from "@/utils/cn";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

const productFeatures = [
  {
    header: "Scalable Cabinet Options",
    text: {
      "Unit System": [
        "Compact and stackable with flat sides and disappearing doors for efficient use of space.",
        "Ideal for smaller labs or those needing expandable storage.",
      ],
      "Cabinet System": [
        "Larger capacity with additional drawer space for card indexes.",
        "Ideal for labs with extensive slide collections.",
        "Piano-hinged doors ensure longevity and tight seals.",
      ],
    },
    image: StackingCabinets,
  },
  {
    header: "Removable Aluminum Trays",
    text: [
      "Each tray holds 20 standard microscope slides (75x25mm) securely.",
      "Trays are easily interchangeable for fast access and seamless workflow.",
      "Durable aluminum material ensures for long lasting performance.",
    ],
    image: Trays,
  },
  {
    header: "Indexed Design for Maximum Efficiency",
    text: [
      "Numbered compartments keep each sample accessible and organized.",
      "Option to stack two slides per compartment for added capacity.",
      "Replacement trays available to expand or maintain your storage needs.",
    ],
    image: IndexedStroage,
  },
];

export default function ProductFeatures() {
  const [selectedProductFeature, setSelectedProductFeature] = useState(productFeatures[0]);

  return (
    <>
      {/* DESKTOP VERSION */}
      <div className="mr-auto ml-auto hidden max-w-[1500px] md:block">
        <ul className="relative flex font-mono">
          {productFeatures.map((feature, i) => {
            const isActive = selectedProductFeature?.header === feature.header;
            return (
              <li key={i} className="relative flex-1 cursor-pointer uppercase">
                {/* sliding pill background for tabs */}
                {isActive && (
                  <motion.div
                    layoutId="tabBg" // shared ID
                    className="absolute inset-0 m-1 rounded-xs bg-primary text-accent"
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 80,
                    }}
                  />
                )}

                <button
                  onClick={() => setSelectedProductFeature(feature)}
                  className={cn(
                    "relative z-10 h-20 w-full border border-primary p-3 text-center text-sm uppercase",
                    isActive && "text-accent",
                  )}
                >
                  {feature.header}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="mt-20 flex flex-col items-center justify-between px-5 pb-16 lg:flex-row">
          {/* handle instances where selectedProductFeature.text is an object vs an array*/}
          <ul className="mx-auto mb-10 flex flex-col gap-16 px-4 sm:px-5 lg:mb-0 lg:w-[50%] lg:px-10">
            <AnimatePresence mode="wait">
              {Array.isArray(selectedProductFeature?.text)
                ? selectedProductFeature.text.map((text, i) => (
                    <motion.li
                      key={`${selectedProductFeature.header}-${i}`}
                      initial={{ opacity: 0, y: 25 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 25 }}
                      transition={{ duration: 0.4 }}
                      className="text-lg md:text-2xl"
                    >
                      <div className="flex items-center">
                        <p className="mr-10 text-5xl text-secondary">0{i + 1}</p>
                        <span className="text-xl leading-10 md:text-2xl">{text}</span>
                      </div>
                    </motion.li>
                  ))
                : Object.entries(selectedProductFeature!.text).map(([heading, arr], i) => (
                    <motion.li
                      key={`${selectedProductFeature?.header}-${i}`}
                      initial={{ opacity: 0, y: 25 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 25 }}
                      transition={{ duration: 0.4 }}
                    >
                      <h3 className="mb-5 font-mono text-3xl text-secondary uppercase">
                        {heading}
                      </h3>
                      {arr.map((line, j) => (
                        <p key={j} className="mb-5 text-2xl">
                          {line}
                        </p>
                      ))}
                    </motion.li>
                  ))}
            </AnimatePresence>
          </ul>
          <AnimatePresence mode="wait">
            <motion.div
              key={`${selectedProductFeature?.header}`}
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 25 }}
              transition={{ duration: 0.4 }}
              className="relative flex h-[35vw] min-h-[500px] w-[35vw] min-w-[500px] items-center"
            >
              {selectedProductFeature?.image && (
                <img src={selectedProductFeature.image.src} alt="product image" />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* MOBILE VERSION */}
      <Accordion
        type="single"
        collapsible
        className="mx-auto w-full max-w-xl rounded-xs border-1 border-primary bg-card shadow md:hidden"
      >
        <AccordionItem value="item-1">
          <AccordionTrigger className="p-5 font-mono text-xl font-normal uppercase">
            removable aluminum trays
          </AccordionTrigger>
          <AccordionContent className="p-10">
            <div>
              <div className="mb-5 flex items-center">
                <p className="mr-10 text-3xl text-secondary">01</p>
                <span className="text-left text-xl leading-8">
                  Each tray holds 20 standard microscope slides (75x25mm) securely.
                </span>
              </div>
              <div className="mb-5 flex items-center">
                <p className="mr-10 text-3xl text-secondary">02</p>
                <span className="text-left text-xl leading-8">
                  Trays are easily interchangeable for fast access and seamless workflow.
                </span>
              </div>
              <div className="mb-5 flex items-center">
                <p className="mr-10 text-3xl text-secondary">03</p>
                <span className="text-left text-xl leading-8">
                  Durable aluminum material ensures for long lasting performance.
                </span>
              </div>
            </div>
            <Image src={Trays} alt="removable aluminum trays"></Image>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="item-2">
          <AccordionTrigger className="p-5 font-mono text-xl font-normal uppercase">
            Indexed Design for maximum efficiency
          </AccordionTrigger>
          <AccordionContent className="p-10">
            <div>
              <div className="mb-5 flex items-center">
                <p className="mr-10 text-3xl text-secondary">01</p>
                <span className="text-left text-xl leading-8">
                  Numbered compartments keep each sample accessible and organized.
                </span>
              </div>
              <div className="mb-5 flex items-center">
                <p className="mr-10 text-3xl text-secondary">02</p>
                <span className="text-left text-xl leading-8">
                  Option to stack two slides per compartment for added capacity.
                </span>
              </div>
              <div className="mb-5 flex items-center">
                <p className="mr-10 text-3xl text-secondary">03</p>
                <span className="text-left text-xl leading-8">
                  Replacement trays available to expand or maintain your storage needs.
                </span>
              </div>
            </div>
            <Image src={IndexedStroage} alt="removable aluminum trays"></Image>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="item-3">
          <AccordionTrigger className="p-5 font-mono text-xl font-normal uppercase">
            scalable cabinet items
          </AccordionTrigger>
          <AccordionContent className="p-10">
            <div>
              <div className="mb-10 flex flex-col text-left">
                <h3 className="mb-5 font-mono text-3xl text-secondary uppercase">Unit System</h3>
                <p className="mb-5 text-xl leading-8">
                  Compact and stackable with flat sides and disappearing doors for efficient use of
                  space.
                </p>
                <p className="mb-5 text-xl leading-8">
                  Ideal for smaller labs or those needing expandable storage.
                </p>
              </div>
              <div className="flex flex-col text-left">
                <h3 className="mb-5 font-mono text-3xl text-secondary uppercase">Cabinet System</h3>
                <p className="mb-5 text-xl leading-8">
                  Larger capacity with additional drawer space for card indexes.
                </p>
                <p className="mb-5 text-xl leading-8">
                  Ideal for labs with extensive slide collections.
                </p>
                <p className="mb-5 text-xl leading-8">
                  Piano-hinged doors ensure longevity and tight seals.
                </p>
              </div>
            </div>
            <img src={StackingCabinets.src} alt="cabinet"></img>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
}
